import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, MessageCircle, CheckCircle2, Sparkles, Gem, X } from 'lucide-react';
import { apiClient } from '../../api/client';
import { FIRST_NFT_SEQUENCE } from '../../constants/product';

/**
 * JourneyChallengeCard — the teen's 20-challenge journey spine on the
 * dashboard. Shows the CURRENT journey challenge (assigned from their
 * Maya/OCEAN answers), lets them practice it with Neo, and — crucially —
 * gives them the "I did it" completion flow that dispatches their
 * meme (1-12) or mintable NFT (13+) reward. This card closed audit
 * GAP B: the completion endpoint existed but no consumer UI called it.
 */
const JourneyChallengeCard = () => {
  const navigate = useNavigate();
  const [cur, setCur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReflect, setShowReflect] = useState(false);
  const [reflection, setReflection] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { kind: 'depth'|'charter'|'error', message, prompts? }
  const [celebration, setCelebration] = useState(null); // completion result

  const load = useCallback(() => {
    apiClient.request('/api/schools/journey/current')
      .then((d) => setCur(d && d.has_journey ? d : null))
      .catch(() => setCur(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !cur) return null;

  const wordCount = reflection.trim().split(/\s+/).filter(Boolean).length;
  const isPivotalNext = (cur.sequence || 0) === FIRST_NFT_SEQUENCE;
  const isNftTier = (cur.sequence || 0) >= FIRST_NFT_SEQUENCE;

  const submitCompletion = async () => {
    if (submitting) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await apiClient.request('/api/schools/journey/complete', {
        method: 'POST',
        body: JSON.stringify({ challenge_id: cur.challenge_id, reflection }),
      });
      if (res.status === 'needs_more_depth') {
        setFeedback({ kind: 'depth', message: res.message, prompts: res.prompts || [] });
      } else if (res.status === 'completed') {
        setCelebration(res);
        setShowReflect(false);
        setReflection('');
        load();
      }
    } catch (err) {
      const detail = err?.data?.detail || err?.detail;
      if (detail && detail.code === 'charter_not_active') {
        setFeedback({ kind: 'charter', message: detail.message });
      } else {
        setFeedback({ kind: 'error', message: 'Something went wrong — try again in a moment.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pivotal = celebration && celebration.actual_sequence === FIRST_NFT_SEQUENCE;

  return (
    <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl p-5 border border-violet-200" data-testid="journey-challenge-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={18} className="text-violet-600" />
          <h3 className="text-sm font-bold text-gray-900">My Challenge Journey</h3>
        </div>
        <span className="text-xs font-semibold text-violet-600" data-testid="journey-progress-label">
          {cur.sequence} / 20
        </span>
      </div>

      {/* progress bar */}
      <div className="h-1.5 bg-white rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full transition-all"
             style={{ width: `${Math.min(100, ((cur.sequence - 1) / 20) * 100)}%` }} />
      </div>

      <p className="text-sm font-bold text-gray-900 mb-1" data-testid="journey-challenge-title">
        {cur.title || cur.theme_label || `Challenge ${cur.sequence}`}
      </p>
      {(cur.core_action || cur.description) && (
        <p className="text-xs text-gray-600 mb-3">{cur.core_action || cur.description}</p>
      )}
      {isPivotalNext && (
        <p className="text-xs font-semibold text-fuchsia-600 mb-3 flex items-center gap-1">
          <Gem size={12} /> Complete this one and your first MINTABLE NFT drops!
        </p>
      )}
      {isNftTier && !isPivotalNext && (
        <p className="text-xs font-semibold text-violet-500 mb-3 flex items-center gap-1">
          <Gem size={12} /> Mintable NFT reward
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => navigate(`/conversations?challenge_id=${cur.challenge_id}`)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-full transition-colors"
          data-testid="journey-practice-btn"
        >
          <MessageCircle size={14} /> Practice with Neo
        </button>
        <button
          onClick={() => { setShowReflect((v) => !v); setFeedback(null); }}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-violet-300 hover:bg-violet-100 text-violet-700 text-xs font-bold rounded-full transition-colors"
          data-testid="journey-i-did-it-btn"
        >
          <CheckCircle2 size={14} /> I did it!
        </button>
      </div>

      {showReflect && (
        <div className="mt-3" data-testid="journey-reflection-panel">
          <textarea
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            placeholder="Tell me what actually happened — where were you, what did you do, how did it feel? (25+ words)"
            rows={3}
            className="w-full text-sm p-3 rounded-xl border border-violet-200 focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
            data-testid="journey-reflection-input"
          />
          <div className="flex items-center justify-between mt-2">
            <span className={`text-[11px] ${wordCount >= 25 ? 'text-green-600' : 'text-gray-400'}`}>{wordCount} / 25 words</span>
            <button
              onClick={submitCompletion}
              disabled={submitting || wordCount < 5}
              className="px-4 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white text-xs font-bold rounded-full transition-colors"
              data-testid="journey-submit-completion-btn"
            >
              {submitting ? 'Checking…' : 'Complete challenge'}
            </button>
          </div>
          {feedback && (
            <div className={`mt-2 text-xs p-3 rounded-xl ${feedback.kind === 'charter' ? 'bg-amber-50 text-amber-700' : 'bg-violet-100 text-violet-700'}`} data-testid="journey-feedback">
              <p className="font-semibold">{feedback.message}</p>
              {(feedback.prompts || []).map((p) => <p key={p} className="mt-1">• {p}</p>)}
              {feedback.kind === 'charter' && (
                <button onClick={() => navigate('/charter')} className="mt-2 underline font-bold">Go to Charter</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Completion celebration — full-screen for the pivotal moment (13),
          inline banner for everything else. */}
      {celebration && (
        pivotal ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="pivotal-celebration-overlay" onClick={() => setCelebration(null)}>
            <div className="bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-3xl p-8 max-w-md text-center text-white shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
              <Sparkles size={44} className="mx-auto mb-3 text-yellow-300" />
              <h2 className="text-2xl font-black mb-2">The Pivotal Moment!</h2>
              <p className="text-sm text-violet-100 mb-4">
                Challenge {FIRST_NFT_SEQUENCE} complete — you've just earned your first <span className="font-bold text-yellow-300">MINTABLE NFT</span>, created uniquely for you from everything you told Maya. From here on, every challenge mints real treasure into your Vault.
              </p>
              <button onClick={() => setCelebration(null)} className="px-6 py-2 bg-white text-violet-700 text-sm font-bold rounded-full" data-testid="pivotal-celebration-close">
                Let's keep going
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl flex items-start gap-2" data-testid="journey-completion-banner">
            <Sparkles size={16} className="text-green-600 mt-0.5 shrink-0" />
            <p className="text-xs text-green-700 font-semibold flex-1">
              Challenge {celebration.actual_sequence} complete!{' '}
              {celebration.reward?.type === 'nft'
                ? 'A mintable NFT is being created for your Vault.'
                : 'Your custom trophy card is being created — check your Vault soon.'}
            </p>
            <button onClick={() => setCelebration(null)} data-testid="journey-completion-dismiss"><X size={14} className="text-green-400" /></button>
          </div>
        )
      )}
    </div>
  );
};

export default JourneyChallengeCard;
